import { PaymentMethodEnum } from '../enums/PaymentMethodEnum';
import { SaleItemKindEnum } from '../enums/SaleItemKindEnum';
import { ILeadSale } from '../entities/ILeadSale';
import { ILeadSaleInstallment } from '../entities/ILeadSaleInstallment';
import { ILeadSalesSummary } from '../entities/ILeadSalesSummary';
import { httpClient } from './helper/httpClient';

export interface IRegisterSaleItemParams {
  kind: SaleItemKindEnum;
  id: string;
  quantity: number;
}

export interface IRegisterSaleInstallmentParams {
  amountInCents: number;
  dueDate: string;
}

export interface IRegisterLeadSaleParams {
  soldAt: string;
  totalPrice: number;
  installmentsCount: number;
  paymentMethod: PaymentMethodEnum;
  notes?: string;
  items: IRegisterSaleItemParams[];
  firstDueDate?: string;
  installments?: IRegisterSaleInstallmentParams[];
}

export interface IInstallmentReceipt {
  fileName: string;
  base64: string;
}

export interface IGetLeadSalesSummaryParams {
  from: string;
  to: string;
  paymentMethods?: PaymentMethodEnum[];
  serviceIds?: string[];
  packageIds?: string[];
}

class LeadSaleService {
  private _baseLeadURL = 'leads';

  private _baseSummaryURL = 'lead-sales';

  getByLead(leadId: string): Promise<ILeadSale[]> {
    return httpClient.get<null, ILeadSale[]>(
      `${this._baseLeadURL}/${leadId}/sales`,
    );
  }

  getSummary(params: IGetLeadSalesSummaryParams): Promise<ILeadSalesSummary> {
    const search = new URLSearchParams();
    search.set('from', params.from);
    search.set('to', params.to);
    if (params.paymentMethods?.length) {
      search.set('paymentMethod', params.paymentMethods.join(','));
    }
    if (params.serviceIds?.length) {
      search.set('serviceId', params.serviceIds.join(','));
    }
    if (params.packageIds?.length) {
      search.set('packageId', params.packageIds.join(','));
    }
    return httpClient.get<null, ILeadSalesSummary>(
      `${this._baseSummaryURL}/summary?${search.toString()}`,
    );
  }

  getById(leadId: string, saleId: string): Promise<ILeadSale> {
    return httpClient.get<null, ILeadSale>(
      `${this._baseLeadURL}/${leadId}/sales/${saleId}`,
    );
  }

  register(leadId: string, data: IRegisterLeadSaleParams): Promise<ILeadSale> {
    return httpClient.post<null, ILeadSale>(
      `${this._baseLeadURL}/${leadId}/sales`,
      data,
    );
  }

  cancel(leadId: string, saleId: string): Promise<void> {
    return httpClient.delete(`${this._baseLeadURL}/${leadId}/sales/${saleId}`);
  }

  markInstallmentPaid(
    leadId: string,
    saleId: string,
    installmentId: string,
  ): Promise<ILeadSaleInstallment> {
    return httpClient.patch<null, ILeadSaleInstallment>(
      `${this._baseLeadURL}/${leadId}/sales/${saleId}/installments/${installmentId}/pay`,
    );
  }

  getInstallmentReceipt(
    leadId: string,
    saleId: string,
    installmentId: string,
  ): Promise<IInstallmentReceipt> {
    return httpClient.get<null, IInstallmentReceipt>(
      `${this._baseLeadURL}/${leadId}/sales/${saleId}/installments/${installmentId}/receipt`,
    );
  }
}

export default new LeadSaleService();
